import { type Infer, v } from "convex/values";
import { GLOBAL_ROLES } from "../../../shared/auth/roles";
import { FILE_OWNER_TYPES } from "../../constants/fileOwners.lib";
import { LOADING_TRUCK_TYPE, RETIRED_TRUCK_TYPE } from "../../constants/types.lib";
import { literalUnion } from "./literalUnion.lib";

export const globalRoles = GLOBAL_ROLES;
export const globalRoleValidator = v.union(
  ...globalRoles.map((role) => v.literal(role)),
);
export type GlobalRole = Infer<typeof globalRoleValidator>;

export const orgRoleValidator = v.union(
  v.literal("owner"),
  v.literal("admin"),
  v.literal("member"),
  v.literal("sales"),
  v.literal("warehouse"),
  v.literal("accounting"),
  v.literal("read_only"),
);
export type OrgRole = Infer<typeof orgRoleValidator>;

export const assignableOrgRoleValidator = v.union(
  v.literal("admin"),
  v.literal("member"),
);
export type AssignableOrgRole = Infer<typeof assignableOrgRoleValidator>;

export const userRoles = GLOBAL_ROLES;
export const userRoleValidator = globalRoleValidator;

export const companyTypeValidator = v.union(
  v.literal("vendor"),
  v.literal("buyer"),
);
export type CompanyType = Infer<typeof companyTypeValidator>;

export const entityTypeValidator = v.union(
  v.literal("business"),
  v.literal("individual"),
);
export type EntityType = Infer<typeof entityTypeValidator>;

export const businessStructureValidator = v.union(
  v.literal("sole_proprietorship"),
  v.literal("single_member_llc"),
  v.literal("multi_member_llc"),
  v.literal("private_partnership"),
  v.literal("private_corporation"),
  v.literal("public_corporation"),
  v.literal("incorporated_non_profit"),
  v.literal("unincorporated_non_profit"),
  v.literal("government_instrumentality"),
  v.literal("governmental_unit"),
  v.literal("tax_exempt_government_instrumentality"),
  v.literal("unincorporated_association"),
  v.literal("public_partnership"),
);
export type BusinessStructure = Infer<typeof businessStructureValidator>;

export const accountTypeValidator = v.union(
  v.literal("buyer"),
  v.literal("vendor"),
  v.literal("freight_carrier"),
);
export type AccountType = Infer<typeof accountTypeValidator>;

export const accountTypeFlagsValidator = v.object({
  prospect: v.optional(v.boolean()),
  buyer: v.optional(v.boolean()),
  vendor: v.optional(v.boolean()),
  freightCarrier: v.optional(v.boolean()),
});
export type AccountTypeFlags = Infer<typeof accountTypeFlagsValidator>;

export const accountTypeFlagsActivationValidator = v.object({
  buyer: v.optional(v.number()),
  vendor: v.optional(v.number()),
  freightCarrier: v.optional(v.number()),
});
export type AccountTypeFlagsActivation = Infer<
  typeof accountTypeFlagsActivationValidator
>;

export const accountTypeFlagKeys = [
  "prospect",
  "buyer",
  "vendor",
  "freightCarrier",
] as const;

export const accountTypeFlagKeyValidator = v.union(
  v.literal("prospect"),
  v.literal("buyer"),
  v.literal("vendor"),
  v.literal("freightCarrier"),
);

export const accountTypeFlipReasonValidator = v.union(
  v.literal("initial_signup"),
  v.literal("rfq_intake"),
  v.literal("first_order"),
  v.literal("first_product_listed"),
  v.literal("first_vendor_quote"),
  v.literal("first_freight_job"),
  v.literal("stripe_connect_started"),
  v.literal("vendor_upgrade"),
  v.literal("admin_manual"),
  v.literal("backfill"),
);
export type AccountTypeFlipReason = Infer<
  typeof accountTypeFlipReasonValidator
>;

export const userStatusValidator = v.union(
  v.literal("active"),
  v.literal("inactive"),
  v.literal("invited"),
  v.literal("suspended"),
  v.literal("pending_claim"),
);
export type UserStatus = Infer<typeof userStatusValidator>;

export const paymentStatusValidator = v.union(
  v.literal("pending"),
  v.literal("authorized"),
  v.literal("paid"),
  v.literal("refunded"),
  v.literal("failed"),
);
export type PaymentStatus = Infer<typeof paymentStatusValidator>;

export {
  orderStageValidator,
  orderStageInputValidator,
  orderPaymentStatusValidator,
  orderFulfillmentStatusValidator,
} from "./orderEnums.lib";
export type {
  OrderStage,
  OrderPaymentStatus,
  OrderFulfillmentStatus,
} from "./orderEnums.lib";

export {
  documentTypeValidator,
  documentSequenceTypeValidator,
  documentStatusValidator,
} from "./documentEnums.lib";
export type { DocumentType, DocumentStatus } from "./documentEnums.lib";

export const rfqTypeValidator = v.union(
  v.literal("buy"),
  v.literal("sell"),
  v.literal("recycle"),
);
export const rfqTypeSchemaValidator = rfqTypeValidator;
export type RfqType = Infer<typeof rfqTypeValidator>;

export const rfqPricingTypeValidator = v.union(
  v.literal("buyer_pays"),
  v.literal("buyer_receives_rebate"),
  v.literal("market_rate"),
);
export type RfqPricingType = Infer<typeof rfqPricingTypeValidator>;

export const leadStatusValidator = v.union(
  v.literal("new"),
  v.literal("confirmed"),
  v.literal("contacted"),
  v.literal("quoted"),
  v.literal("follow_up"),
  v.literal("won"),
  v.literal("lost"),
);
export type LeadStatus = Infer<typeof leadStatusValidator>;

export const leadTypeValidator = v.union(
  v.literal("buy"),
  v.literal("sell"),
  v.literal("recycle"),
  v.literal("waste"),
  v.literal("inquiry"),
);
export type LeadType = Infer<typeof leadTypeValidator>;

export {
  supportAgentProviderValidator,
  supportGithubLinkStates,
  supportGithubLinkStateValidator,
  supportKindValidator,
  supportPriorityValidator,
  supportProjectStatusValidator,
  supportRequestStatusValidator,
  supportRequestStatuses,
  supportResolutionCodeValidator,
  supportSourceValidator,
  supportTicketTypeValidator,
  supportWorkStateValidator,
  supportWorkStates,
  type SupportAgentProvider,
  type SupportGithubLinkState,
  type SupportKind,
  type SupportPriority,
  type SupportProjectStatus,
  type SupportRequestStatus,
  type SupportResolutionCode,
  type SupportSource,
  type SupportTicketType,
  type SupportWorkState,
} from "./support.lib";

export const ownerTypeValidator = v.union(
  ...FILE_OWNER_TYPES.map((ownerType) => v.literal(ownerType)),
);
export type OwnerType = Infer<typeof ownerTypeValidator>;

export const searchResultTypeValidator = v.union(
  v.literal("order"),
  v.literal("quote"),
  v.literal("rfq"),
  v.literal("product"),
  v.literal("document"),
  v.literal("user"),
  v.literal("company"),
  v.literal("lead"),
  v.literal("city"),
  v.literal("navigation"),
);
export type SearchResultType = Infer<typeof searchResultTypeValidator>;

export const leadSourceValidator = v.union(
  v.literal("organic"),
  v.literal("rfq"),
  v.literal("direct_signup"),
  v.literal("referral"),
  v.literal("admin"),
  v.literal("marketing_campaign"),
  v.literal("partner"),
  v.literal("guest_checkout"),
);
export type LeadSource = Infer<typeof leadSourceValidator>;

export const relationshipTypeValidator = v.union(
  v.literal("customer"),
  v.literal("vendor"),
  v.literal("partner"),
  v.literal("prospect"),
);
export type RelationshipType = Infer<typeof relationshipTypeValidator>;

export const relationshipStatusValidator = v.union(
  v.literal("active"),
  v.literal("inactive"),
  v.literal("churned"),
);
export type RelationshipStatus = Infer<typeof relationshipStatusValidator>;

export const contactTypeValidator = v.union(
  v.literal("lead"),
  v.literal("prospect"),
  v.literal("buyer"),
  v.literal("vendor"),
  v.literal("freight_carrier"),
  v.literal("both"),
  v.literal("customer"),
  v.literal("supplier"),
);
export type ContactType = Infer<typeof contactTypeValidator>;

export const paymentPreferenceValidator = v.union(
  v.literal("pay_me"),
  v.literal("i_pay"),
  v.literal("negotiable"),
);
export type PaymentPreference = Infer<typeof paymentPreferenceValidator>;

export const sourceIndustryValidator = v.union(
  v.literal("warehousing-3pl"),
  v.literal("manufacturing"),
  v.literal("retail-ecommerce"),
  v.literal("food-beverage"),
  v.literal("automotive"),
  v.literal("pharmaceuticals"),
  v.literal("logistics"),
  v.literal("other"),
);
export type SourceIndustry = Infer<typeof sourceIndustryValidator>;

export const productSourceValidator = v.union(
  v.literal("public-form"),
  v.literal("dashboard"),
  v.literal("api"),
  v.literal("import"),
);
export type ProductSource = Infer<typeof productSourceValidator>;

export const listingStatusValidator = v.union(
  v.literal("draft"),
  v.literal("pending"),
  v.literal("published"),
  v.literal("rejected"),
  v.literal("archived"),
);
export type ListingStatus = Infer<typeof listingStatusValidator>;

export const loadingTruckTypeValidator = literalUnion(LOADING_TRUCK_TYPE);
export type LoadingTruckType = Infer<typeof loadingTruckTypeValidator>;

export const standardTruckTypeValidator = v.union(...loadingTruckTypeValidator.members, ...literalUnion(RETIRED_TRUCK_TYPE).members);
export type StandardTruckType = Infer<typeof standardTruckTypeValidator>;

export const freightTruckTypeValidator = v.union(...standardTruckTypeValidator.members, v.literal("reefer"));
export type FreightTruckType = Infer<typeof freightTruckTypeValidator>;

export const deliveryFrequencyValidator = v.union(
  v.literal("one-time"),
  v.literal("weekly"),
  v.literal("bi-weekly"),
  v.literal("monthly"),
  v.literal("quarterly"),
  v.literal("custom"),
);
export type DeliveryFrequency = Infer<typeof deliveryFrequencyValidator>;

export const subscriptionTierValidator = v.union(
  v.literal("free"),
  v.literal("starter"),
  v.literal("pro"),
  v.literal("enterprise"),
);
export type SubscriptionTier = Infer<typeof subscriptionTierValidator>;

export const subscriptionStatusValidator = v.union(
  v.literal("active"),
  v.literal("trialing"),
  v.literal("past_due"),
  v.literal("canceled"),
  v.literal("incomplete"),
  v.literal("incomplete_expired"),
  v.literal("unpaid"),
  v.literal("paused"),
  v.literal("none"),
);
export type SubscriptionStatus = Infer<typeof subscriptionStatusValidator>;

export const billingIntervalValidator = v.union(
  v.literal("monthly"),
  v.literal("annual"),
);
export type BillingInterval = Infer<typeof billingIntervalValidator>;

export const buyerInvoiceStatusValidator = v.union(
  v.literal("overdue"),
  v.literal("partly_paid"),
  v.literal("open"),
  v.literal("not_yet_due"),
  v.literal("paid"),
);
export type BuyerInvoiceStatus = Infer<typeof buyerInvoiceStatusValidator>;

export const stripeRecurringIntervalValidator = v.union(
  v.literal("day"),
  v.literal("week"),
  v.literal("month"),
  v.literal("year"),
);
export type StripeRecurringInterval = Infer<
  typeof stripeRecurringIntervalValidator
>;

export const companyCreditStatusValidator = v.union(
  v.literal("none"),
  v.literal("pending"),
  v.literal("approved"),
  v.literal("suspended"),
);
export type CompanyCreditStatus = Infer<typeof companyCreditStatusValidator>;

export const creditApplicationStatusValidator = v.union(
  v.literal("pending"),
  v.literal("approved"),
  v.literal("denied"),
  v.literal("expired"),
);
export type CreditApplicationStatus = Infer<
  typeof creditApplicationStatusValidator
>;

export const quoteClaimStatusValidator = v.union(
  v.literal("pending"),
  v.literal("claimed"),
  v.literal("expired"),
  v.literal("revoked"),
);
export type QuoteClaimStatus = Infer<typeof quoteClaimStatusValidator>;
